import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getConversations, getConversationMessages } from '../services/api';
import { formatMessageTimestamp } from '../utils/dateLocale';
import './ConversationsPage.css';

// Read-only WhatsApp message log. Conversations are keyed by phone; messages have
// a direction ("in" = del cliente, "out" = del sistema), un `body` (texto del mensaje o
// el valor tapeado en un interactive) y un `type` (text | interactive | template | location |
// flow_reply). Inbound y outbound se persisten (NER-2501): el thread muestra la conversación
// completa estilo chat. Para templates se muestra el nombre del template; para tipos no-texto
// (lista/botones, ubicación, formulario) se muestra una etiqueta de tipo.
function ConversationsPage() {
  const { t, i18n } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getConversations();
        if (!active) return;
        setConversations(data?.conversations || []);
      } catch (err) {
        console.error('Error loading conversations:', err);
        if (active) {
          setConversations([]);
          setError(t('conversations.loadError'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [t]);

  const selectConversation = useCallback(
    async (conversationId) => {
      setSelectedId(conversationId);
      setMessagesLoading(true);
      setMessages([]);
      try {
        const data = await getConversationMessages(conversationId);
        setMessages(data?.messages || []);
      } catch (err) {
        console.error('Error loading messages:', err);
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    },
    []
  );

  const formatTime = (value) => formatMessageTimestamp(value, i18n.language, t);

  // Etiqueta de tipo de contenido para los mensajes que no son texto libre. `template` ya muestra
  // su nombre (message-template); `text` es el default y no necesita etiqueta. Devuelve null cuando
  // no corresponde mostrar badge. i18n: conversations.types.<type> (con fallback a la key cruda).
  const typeLabel = (type) => {
    if (!type || type === 'text' || type === 'template') return null;
    return t(`conversations.types.${type}`, type);
  };

  const selectedConversation = conversations.find((c) => c.conversationId === selectedId);

  return (
    <div className="conversations-page">
      <div className="page-header">
        <h1>{t('conversations.title')}</h1>
      </div>

      <p className="conversations-caveat">{t('conversations.inboundPendingNote')}</p>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : conversations.length === 0 ? (
        <div className="empty-state">{t('conversations.empty')}</div>
      ) : (
        <div className="conversations-layout">
          <aside className="conversations-list card">
            <ul>
              {conversations.map((c) => (
                <li key={c.conversationId}>
                  <button
                    type="button"
                    className={`conversation-item ${
                      c.conversationId === selectedId ? 'active' : ''
                    }`}
                    onClick={() => selectConversation(c.conversationId)}
                  >
                    <div className="conversation-item-top">
                      <span className="conversation-name">
                        {c.customerName || c.customerPhone || c.conversationId}
                      </span>
                      <span className="conversation-time">{formatTime(c.lastMessageAt)}</span>
                    </div>
                    {c.lastMessagePreview && (
                      <span className="conversation-preview">{c.lastMessagePreview}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="conversation-thread card">
            {!selectedId ? (
              <div className="empty-state">{t('conversations.selectPrompt')}</div>
            ) : messagesLoading ? (
              <div className="loading">{t('common.loading')}</div>
            ) : (
              <>
                <header className="thread-header">
                  {selectedConversation?.customerName ||
                    selectedConversation?.customerPhone ||
                    selectedId}
                </header>
                {messages.length === 0 ? (
                  <div className="empty-state">{t('conversations.noMessages')}</div>
                ) : (
                  <div className="thread-messages">
                    {messages.map((m) => (
                      <div
                        key={m.messageId}
                        className={`message-bubble message-bubble--${
                          m.direction === 'in' ? 'in' : 'out'
                        }`}
                      >
                        {m.templateName && (
                          <span className="message-template">{m.templateName}</span>
                        )}
                        {typeLabel(m.type) && (
                          <span className="message-type">{typeLabel(m.type)}</span>
                        )}
                        {m.body ? (
                          <span className="message-body">{m.body}</span>
                        ) : (
                          // NER-2689: nunca dejar la burbuja vacía. El backend ahora persiste un
                          // body legible para location ('📍 dirección'); este fallback cubre items
                          // legacy (location sin body) y cualquier tipo sin contenido renderizable.
                          !m.templateName && (
                            <span className="message-body message-body--placeholder">
                              {m.type === 'location'
                                ? t('conversations.locationFallback')
                                : t('conversations.emptyBody')}
                            </span>
                          )
                        )}
                        <span className="message-meta">
                          {formatTime(m.timestamp)}
                          {m.direction === 'out' && m.status ? ` · ${m.status}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default ConversationsPage;
